import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trendlineStrategyService } from '../config/api';

// Default fallbacks in case API fails
const DEFAULT_INDICATOR_TYPES = {
    RSI: { value: 'RSI', label: 'Relative Strength Index (RSI)' },
    SMA: { value: 'SMA', label: 'Simple Moving Average (SMA)' },
    EMA: { value: 'EMA', label: 'Exponential Moving Average (EMA)' },
    WMA: { value: 'WMA', label: 'Weighted Moving Average (WMA)' },
    MACD: { value: 'MACD', label: 'MACD' },
};

const DEFAULT_COMPARISON_OPERATORS = {
    GREATER_THAN: { value: 'GREATER_THAN', label: 'Greater Than (>)', symbol: '>' },
    LESS_THAN: { value: 'LESS_THAN', label: 'Less Than (<)', symbol: '<' },
    CROSSED_UP: { value: 'CROSSED_UP', label: 'Crossed Above', symbol: '↑' },
    CROSSED_DOWN: { value: 'CROSSED_DOWN', label: 'Crossed Below', symbol: '↓' },
};

const DEFAULT_LOGICAL_OPERATORS = {
    AND: { value: 'AND', label: 'AND (All conditions must be true)' },
    OR: { value: 'OR', label: 'OR (Any condition must be true)' },
};

const DEFAULT_CONDITION_TYPES = {
    INDICATOR: { value: 'INDICATOR', label: 'Technical Indicator' },
    TRENDLINE: { value: 'TRENDLINE', label: 'Trendline' },
};

const SchemaContext = createContext();

export function useSchema() {
    return useContext(SchemaContext);
}

export function SchemaProvider({ children }) {
    const [indicatorTypes, setIndicatorTypes] = useState(DEFAULT_INDICATOR_TYPES);
    const [indicatorSchemas, setIndicatorSchemas] = useState({});
    const [comparisonOperators, setComparisonOperators] = useState(DEFAULT_COMPARISON_OPERATORS);
    const [logicalOperators, setLogicalOperators] = useState(DEFAULT_LOGICAL_OPERATORS);
    const [conditionTypes, setConditionTypes] = useState(DEFAULT_CONDITION_TYPES);
    const [ruleTypes, setRuleTypes] = useState([]);
    const [candlestickPatterns, setCandlestickPatterns] = useState([]); // New state for patterns
    const [advancedRulesInfo, setAdvancedRulesInfo] = useState({
        compositeSupported: false,
        candlestickSupported: false,
        voteRuleSupported: false,
        chainRuleSupported: false,
        maxVoteConditions: 10
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch all schemas on mount
    useEffect(() => {
        const fetchSchemas = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch both schemas in parallel
                const [indicatorResponse, rulesResponse] = await Promise.allSettled([
                    trendlineStrategyService.getIndicatorSchemas(),
                    trendlineStrategyService.getRulesSchema(),
                ]);

                // Process indicator schemas
                if (indicatorResponse.status === 'fulfilled' && indicatorResponse.value?.data) {
                    const apiIndicators = indicatorResponse.value.data.indicators;
                    if (apiIndicators && Object.keys(apiIndicators).length > 0) {
                        // Convert API response to dropdown-friendly format
                        const processedIndicators = {};
                        Object.entries(apiIndicators).forEach(([key, info]) => {
                            processedIndicators[key] = {
                                value: key,
                                label: info.displayName || key,
                                description: info.description,
                                parameters: info.parameters,
                                defaultValues: info.defaultValues,
                            };
                        });
                        setIndicatorTypes(processedIndicators);
                        setIndicatorSchemas(apiIndicators);
                    }
                }

                // Process rules schema
                if (rulesResponse.status === 'fulfilled' && rulesResponse.value?.data) {
                    const data = rulesResponse.value.data;

                    // Condition types with their operators
                    if (data.conditionTypes && data.conditionTypes.length > 0) {
                        const processedConditionTypes = {};
                        data.conditionTypes.forEach((ct) => {
                            const processedOps = {};
                            if (ct.operators) {
                                ct.operators.forEach(op => {
                                    processedOps[op.name] = {
                                        value: op.name,
                                        label: op.description,
                                        symbol: op.symbol,
                                        description: op.description,
                                    };
                                });
                            }

                            processedConditionTypes[ct.name] = {
                                value: ct.name,
                                label: ct.displayName || ct.name,
                                description: ct.description,
                                operators: processedOps,
                            };
                        });
                        setConditionTypes(processedConditionTypes);
                    }

                    // Comparison operators
                    if (data.comparisonOperators && data.comparisonOperators.length > 0) {
                        const processedOperators = {};
                        data.comparisonOperators.forEach((op) => {
                            processedOperators[op.name] = {
                                value: op.name,
                                label: `${op.description} (${op.symbol})`,
                                symbol: op.symbol,
                                description: op.description,
                            };
                        });
                        setComparisonOperators(processedOperators);
                    }

                    // Logical operators
                    if (data.logicalOperators && data.logicalOperators.length > 0) {
                        const processedLogical = {};
                        data.logicalOperators.forEach((op) => {
                            processedLogical[op.name] = {
                                value: op.name,
                                label: `${op.name} - ${op.description}`,
                                description: op.description,
                            };
                        });
                        setLogicalOperators(processedLogical);
                    }

                    // Rule types (for trendline triggers)
                    if (data.ruleTypes && data.ruleTypes.length > 0) {
                        setRuleTypes(data.ruleTypes);
                    }

                    // Advanced rules info (CompositeCondition support)
                    if (data.advancedRules) {
                        setAdvancedRulesInfo(data.advancedRules);
                    }

                    // Candlestick patterns (Phase 2)
                    if (data.candlestickPatterns && data.candlestickPatterns.length > 0) {
                        setCandlestickPatterns(data.candlestickPatterns);
                    }
                }
            } catch (err) {
                console.error('Error fetching schemas:', err);
                setError('Failed to load schemas from server. Using defaults.');
            } finally {
                setLoading(false);
            }
        };

        fetchSchemas();
    }, []);

    // Refresh function to re-fetch schemas
    const refreshSchemas = useCallback(async () => {
        setLoading(true);
        try {
            const [indicatorResponse, rulesResponse] = await Promise.all([
                trendlineStrategyService.getIndicatorSchemas(),
                trendlineStrategyService.getRulesSchema(),
            ]);
            // Process responses (same as above)
            // For brevity, just trigger a re-mount effect
            window.location.reload();
        } catch (err) {
            setError('Failed to refresh schemas');
        } finally {
            setLoading(false);
        }
    }, []);

    const value = {
        // Indicator data
        indicatorTypes,
        indicatorSchemas,

        // Rules/operators data
        conditionTypes,
        comparisonOperators,
        logicalOperators,
        ruleTypes,
        candlestickPatterns, // Export candlestick patterns
        advancedRulesInfo,

        // State
        loading,
        error,

        // Actions
        refreshSchemas,
    };

    return (
        <SchemaContext.Provider value={value}>
            {children}
        </SchemaContext.Provider>
    );
}
